import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, Col, Divider, Row } from 'antd';
import { ReactComponent as CloseIcon } from 'app/assets/img/close_icon.svg';
import { CheckoutOrderOverview } from 'app/components/CheckoutOrderOverview';
import { LoadingContainer } from 'app/components/LoadingContainer';
import ModalContainer from 'app/components/ModalContainer';
import StyledButton from 'app/components/StyledButton';
import React, { memo, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { BP_TYPE, DEFAULT_LANGUAGE, TYPE_COOKIE } from 'utils/constants';
import { getCookie } from 'utils/cookies';
import { formatNumber, windowOpenLink } from 'utils/helper';
import { z } from 'zod';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import PaymentInprogressIcon from 'app/assets/img/iconPaymentInprogress.png';
import EditOrderPopup, {
  ADJUST_ORDER_STATE,
  EDIT_FLOW_TYPES,
  EDIT_FORM_VALUES,
  TICKET_TYPES,
} from './components/EditOrderPopup';
import { useCheckout } from './hooks/useCheckout';
import { GET_LIST_PAYMENT_METHOD } from './slice';
import { CheckoutWrapper } from './styled';
interface Props { }

const replacePaymentWithSpan = (
  text: string,
  replaceText: string,
  replaceTo: string,
  style: React.CSSProperties = {},
) => {
  const parts = text.split(replaceText);

  const newText = parts.map((part, index) => (
    <>
      {part}
      {index < parts.length - 1 && <span style={style}>{replaceTo}</span>}
    </>
  ));

  return <>{newText}</>;
};

const formatDuration = (value?: number | string) => {
  if (value === undefined || value === null || value === '') return '';

  if (typeof value === 'string' && value.includes(':')) return value;

  const totalSeconds = Number(value);
  if (Number.isNaN(totalSeconds)) return `${value}`;

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds]
    .map(item => `${item}`.padStart(2, '0'))
    .join(':');
};

const getPaymentDueTimestamp = (paymentDueAt?: any) => {
  const rawValue =
    typeof paymentDueAt === 'object'
      ? paymentDueAt?.datetime ||
      paymentDueAt?.datetimeDMYHIS ||
      paymentDueAt?.datetimeDMY ||
      paymentDueAt?.date
      : paymentDueAt;

  if (!rawValue) return undefined;

  const dueTime = new Date(rawValue).getTime();
  return Number.isNaN(dueTime) ? undefined : dueTime;
};

const getPaymentDueCountdown = (paymentDueAt?: any, now = Date.now()) => {
  const dueTime = getPaymentDueTimestamp(paymentDueAt);
  if (!dueTime) return '-';

  const remainingSeconds = Math.floor((dueTime - now) / 1000);
  if (remainingSeconds <= 0) return '-';

  return formatDuration(remainingSeconds);
};

const formatReservationMoney = (
  amount?: number | string,
  currency?: string,
) => {
  const currencyLabel = currency;
  return `${formatNumber(amount as number ?? 0)} ${currencyLabel}`;
};

export const ConfirmPolicyOrder = memo((props: Props) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [isAccept, setIsAccept] = useState(false);
  const [termTxt, setTermTxt] = useState('');
  const [isShowTimeout, setIsShowTimeout] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [now, setNow] = useState(Date.now());

  const translations = useSelector(selectTranslations);
  const {
    loading,
    totalAmount,
    totalPriceOriginal,
    totalPrice,
    setEnableConfirm,
    orderCheckout,
    totalDiscount,
    groupCart,
    vouchers,
    listCart,
    idOrder,
    payment,
    onSubmitForm,
    bankInfo,
    orderState,
    expiredHours,
    expiredAt,
    isValidPaymentTime,
    order,
    getOrderDetail,
  } = useCheckout();

  const { onCloseDialogNoti } = useGlobalFunction();

  useEffect(() => {
    dispatch(
      GET_LIST_PAYMENT_METHOD({
        ticketType: sessionStorage.getItem('ticketType'),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fetchTerm = async () => {
      const language =
        (await getCookie(TYPE_COOKIE.LANGUAGE)) || DEFAULT_LANGUAGE;
      fetch(`/policy-payment-${language}.txt`)
        .then(response => response.text())
        .then(data => {
          setTermTxt(data);
        });
    };
    fetchTerm();
  }, []);

  useEffect(() => {
    const tick = () => {
      const nextNow = Date.now();
      const checkoutDetail = orderCheckout.current;
      const paymentDueAt = expiredAt ?? checkoutDetail?.expiredAt;
      const dueTimestamp = getPaymentDueTimestamp(paymentDueAt);

      setNow(nextNow);

      if (
        !dueTimestamp ||
        nextNow < dueTimestamp
      ) {
        return;
      }

    };

    const intervalId = window.setInterval(tick, 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [expiredAt, loading, orderCheckout]);

  useEffect(() => {
    if (payment?.code && payment?.code.toLowerCase() === 'banktransfer') {
      if (orderState?.value === 'success') {
        history.push({
          pathname: '/reservation-history',
          state: {
            orderState: 'success',
            payment: payment?.code,
          },
        });
      }
      if (orderState?.value === 'cancelled') {
        history.push({
          pathname: '/reservation-history',
          state: {
            orderState: 'cancelled',
            payment: payment?.code,
          },
        });
      }
    }
  }, [history, payment, orderState]);

  const adjustedPriceOrFOC = !!order?.adjustedPriceOrFOC;

  const editOrderSchema = z.object({
    giftTickets: z.array(
      z
        .object({
          _productId: z.number(),
          amount: z.number().nullable(),
          unitPrice: z.number(),
          maxQuantityFOC: z.number().optional(),
          _original: z.object({
            amount: z.number(),
            unitPrice: z.number(),
          }),
          type: z.union([
            z.literal(TICKET_TYPES.FOC),
            z.literal(TICKET_TYPES.NORMAL_GIFT),
          ]),
        })
        .superRefine((data, ctx) => {
          if (
            typeof data.amount === 'number' &&
            data.type === TICKET_TYPES.FOC &&
            typeof data.maxQuantityFOC === 'number' &&
            data.amount > data.maxQuantityFOC
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.too_big,
              maximum: data.maxQuantityFOC,
              type: 'number',
              inclusive: true,
              path: ['amount'],
              message:
                translations?.['b2b.txt.ticket_limit_exceeded']?.replace(
                  '${max}',
                  data.maxQuantityFOC.toString(),
                ) ?? '',
            });
          }
        }),
    ),
    mainTickets: z.array(
      z
        .object({
          _productId: z.number(),
          amount: z.number(),
          unitPrice: z.number().nullable(),
          _original: z.object({
            amount: z.number(),
            unitPrice: z.number(),
          }),
          type: z.union([
            z.literal(TICKET_TYPES.MAIN),
            z.literal(TICKET_TYPES.ENTRANCE_TICKET),
          ]),
        })
        .superRefine((data, ctx) => {
          if (
            data._original.unitPrice !== 0 &&
            typeof data.unitPrice === 'number' &&
            data.unitPrice < 1
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.too_small,
              minimum: 1,
              type: 'number',
              inclusive: true,
              path: ['unitPrice'],
              message: translations?.['b2b.txt.invalidPrice'] ?? '',
            });
          }
        }),
    ),
    docs: z.any().optional(),
  });

  const editOrderForm = useForm<EDIT_FORM_VALUES>({
    defaultValues: {
      giftTickets: [],
      mainTickets: [],
    },
    resolver: zodResolver(editOrderSchema),
    mode: 'onChange',
  });

  const infoUser = useSelector(selectInfoUser);

  const isProxyBuyer =
    (infoUser?.businessPartner as any)?.bpType === BP_TYPE.PROXY_BUYER;

  const isProxyOrder = !!order?.businessPartnerAllocatedDebt?.id;

  const isEditableInProxyBuyingFlow = isProxyBuyer && isProxyOrder;

  const isHasFOC = listCart?.some(
    item => item?.higherLevelItem === TICKET_TYPES.FOC,
  );
  const isEditableInNormalBuyingFlow =
    !isProxyOrder &&
    isHasFOC &&
    !infoUser?.businessPartner?.notAllowToChangeFOC;

  const isEditable =
    isEditableInProxyBuyingFlow || isEditableInNormalBuyingFlow;

  const isEnableAdjustOrderInProxyBuyingFlow =
    isEditableInProxyBuyingFlow &&
    [ADJUST_ORDER_STATE.PAYMENT_PENDING].includes(orderState?.value) &&
    !adjustedPriceOrFOC;

  const isEnableAdjustOrderInNormalBuyingFlow =
    isEditableInNormalBuyingFlow &&
    [ADJUST_ORDER_STATE.PAYMENT_PENDING].includes(orderState?.value);

  const isEnableAdjustOrder =
    isEnableAdjustOrderInProxyBuyingFlow ||
    isEnableAdjustOrderInNormalBuyingFlow;

  const isHidePaymentDesc = [
    ADJUST_ORDER_STATE.PENDING_APPROVE,
    ADJUST_ORDER_STATE.INIT,
  ].includes(orderState?.value);

  const handleRidrect = async () => {
    windowOpenLink({
      url: '/reservation-history?route=tab-3',
      target: '_self',
    });
  };

  const onOpenEditModal = () => {
    setOpenEditModal(true);
  };

  const onCloseEditModal = () => {
    setOpenEditModal(false);
  };

  const reservationItem = listCart?.[0] as any;
  const reservationNo = `${idOrder ?? order?.reservationNo ?? ''}`;
  const restaurantName =
    reservationItem?.park?.name ||
    order?.storeName ||
    order?.restaurantName ||
    reservationItem?.product?.name ||
    'Beer Plaza';
  const usageDateText = `${reservationItem?.date ?? reservationItem?.selectedDate ?? ''
    }`;
  const usageDate = usageDateText.split(',')?.[0]?.trim() || '--';
  const usageTime =
    order?.reservationTime ||
    order?.bookingTime ||
    order?.time ||
    usageDateText.split(',')?.[1]?.trim() ||
    '--';
  const zoneName = reservationItem?.zone?.name || order?.zoneName || '--';
  const guestAmount =
    reservationItem?.amount ||
    order?.partySize ||
    order?.guestAmount ||
    totalAmount ||
    '--';
  const policyLines = termTxt?.replaceAll('\\n', '\n').split('\n') ?? [];
  const paymentDueAt = expiredAt ?? orderCheckout.current?.expiredAt;
  const holdTimeLabel = getPaymentDueCountdown(paymentDueAt, now);
  const reservationCurrency = order?.currency ?? orderCheckout.current?.currency;
  const paymentTotalLabel = formatReservationMoney(
    totalPrice,
    reservationCurrency,
  );

  return (
    <CheckoutWrapper id="bookticketspay" className="main">
      <div className="bookticketspayContent bookticketsContent checkOut">
        <div className="container custom-container">
          <LoadingContainer isFullPage loading={loading}>
            <ReservationCheckoutGrid>
              <ReservationPolicyCard>
                <PaymentIconWrap>
                  <img src={PaymentInprogressIcon} alt="" />
                </PaymentIconWrap>
                <ReservationStatusTitle>CHỜ THANH TOÁN</ReservationStatusTitle>
                <ReservationCodeLine>
                  Mã đặt bàn <strong>#{reservationNo}</strong>
                </ReservationCodeLine>
                <ReservationNote>
                  Vui lòng đặt cọc bằng phương thức thanh toán{' '}
                  <strong>{payment?.name ?? 'Deposit'}</strong> trong vòng{' '}
                  <strong>{holdTimeLabel}</strong>. Sau thời điểm này không
                  thanh toán, yêu cầu đặt bàn sẽ tự động được huỷ.
                </ReservationNote>
                <ReservationDivider />
                <PolicyTitle>ĐIỀU KHOẢN THANH TOÁN</PolicyTitle>
                <PolicyDescription>
                  Quý khách xác nhận điều khoản thanh toán của SunWorld và thanh
                  toán.
                </PolicyDescription>
                <ReservationPolicyBox>
                  <PolicySubTitle>Điều khoản thanh toán</PolicySubTitle>
                  <PolicyText>
                    {policyLines.map((val, index) => (
                      <div key={index}>{val}</div>
                    ))}
                  </PolicyText>
                </ReservationPolicyBox>
                <ReservationCheckbox
                  checked={isAccept}
                  onChange={() => setIsAccept(prev => !prev)}
                >
                  Tôi đồng ý với mọi điều khoản thanh toán của SunWorld
                </ReservationCheckbox>
                <ReservationActions>
                  <HistoryButton onClick={handleRidrect}>
                    Xem lịch sử đặt bàn
                  </HistoryButton>
                  <ConfirmButton
                    disabled={!isAccept || loading}
                    onClick={() => {
                      if (loading) return;
                      setEnableConfirm(false);
                      setTimeout(() => onSubmitForm(), 100);
                    }}
                  >
                    Xác nhận
                  </ConfirmButton>
                </ReservationActions>
              </ReservationPolicyCard>

              <ReservationSummaryCard>
                <SummaryHeader>
                  <SummaryOrder>
                    Mã đặt bàn <strong>#{reservationNo}</strong>
                  </SummaryOrder>
                  <SummaryTimer>{holdTimeLabel}</SummaryTimer>
                </SummaryHeader>
                <RestaurantTicket>
                  <TicketHead>{restaurantName}</TicketHead>
                  <TicketBody>
                    <TicketName>{restaurantName}</TicketName>
                    <SummaryRow>
                      <span>Ngày sử dụng</span>
                      <strong>{usageDate}</strong>
                    </SummaryRow>
                    <SummaryRow>
                      <span>Khung giờ</span>
                      <strong>{usageTime}</strong>
                    </SummaryRow>
                    <SummaryRow>
                      <span>Số lượng khách</span>
                      <strong>{guestAmount}</strong>
                    </SummaryRow>
                    <SummaryRow>
                      <span>Khu vực</span>
                      <strong>{zoneName}</strong>
                    </SummaryRow>
                  </TicketBody>
                </RestaurantTicket>
                <PaymentSummary>
                  <span>Thanh toán</span>
                  <strong>{paymentTotalLabel}</strong>
                </PaymentSummary>
                <PolicySummaryTitle>Chính sách</PolicySummaryTitle>
                <PolicySummaryItem>
                  Đặt cọc áp dụng cho khung giờ cao điểm
                </PolicySummaryItem>
                <PolicySummaryItem>
                  Cần trừ vào hóa đơn khi thanh toán tại nhà hàng
                </PolicySummaryItem>
              </ReservationSummaryCard>
            </ReservationCheckoutGrid>
            <div className="row" style={{ display: 'none' }}>
              <div className="col-xl-8">
                {/* vnpay deposit */}
                {payment?.code &&
                  payment?.code.toLowerCase() !== 'banktransfer' && (
                    <>
                      <div className="checkOutInfo">
                        <HeaderContainer>
                          <Title>
                            {translations
                              ? translations[
                                'b2c.txt.create-order-number'
                              ]?.replaceAll('${number}', `${idOrder ?? ''}`)
                              : ''}
                          </Title>
                        </HeaderContainer>
                        {!isHidePaymentDesc && (
                          <>
                            {expiredHours && isValidPaymentTime ? (
                              <Description>
                                {translations
                                  ? replacePaymentWithSpan(
                                    translations[
                                    'b2c.txt.pay-order-description'
                                    ],
                                    '${payment}',
                                    payment?.name ?? '',
                                    { fontWeight: 'bold' },
                                  )
                                  : ''}{' '}
                                {translations
                                  ? replacePaymentWithSpan(
                                    translations[
                                    'b2c.txt.pay-order-description-1'
                                    ],
                                    '${hour}',
                                    expiredHours + 'h',
                                    { color: '#E92127' },
                                  )
                                  : ''}
                              </Description>
                            ) : undefined}
                          </>
                        )}
                      </div>
                      <div className="checkOutInfo">
                        {isValidPaymentTime && (
                          <>
                            <HeaderContainer>
                              <Title>
                                {translations
                                  ? translations['b2c.txt.policyTitle']
                                  : ''}
                              </Title>
                            </HeaderContainer>
                            <Description>
                              {translations
                                ? translations['b2b.form-policy.description']
                                : ''}
                            </Description>
                            <StyledPolicy>
                              <Policy>
                                <SubHeaderContainer>
                                  <div style={{ textTransform: 'capitalize' }}>
                                    {translations
                                      ? translations['b2c.txt.paymentPolicy']
                                      : ''}
                                  </div>
                                </SubHeaderContainer>
                                <Description
                                  style={{
                                    textAlign: 'justify',
                                    marginRight: 8,
                                  }}
                                >
                                  {termTxt
                                    ?.replaceAll('\\n', '\n')
                                    .split('\n')
                                    .map((val, index) => (
                                      <div
                                        key={index}
                                        style={{ whiteSpace: 'pre-wrap' }}
                                      >
                                        {val}
                                        <br />
                                      </div>
                                    ))}
                                </Description>
                              </Policy>
                            </StyledPolicy>
                            <Checkbox
                              style={{ margin: '12px 0px' }}
                              onChange={e => {
                                setIsAccept(!isAccept);
                              }}
                            >
                              {translations
                                ? translations['b2c.txt.accept-policy']
                                : ''}
                            </Checkbox>
                            <StyledDivider
                              style={{
                                margin: '20px 0px 25px 0px',
                              }}
                              dashed
                            />
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                              }}
                            >
                              <StyledButton
                                bgColor="white"
                                border="1px solid #808080"
                                textColor="#808080"
                                height={'44px'}
                                onClick={handleRidrect}
                                style={{
                                  marginRight: 16,
                                  width: 'max-content',
                                }}
                              >
                                {translations
                                  ? translations['b2b.txt.back-to-history']
                                  : ''}
                              </StyledButton>
                              <StyledButton
                                disabled={!isAccept || loading}
                                border="none"
                                textColor="#ffffff"
                                onClick={() => {
                                  if (loading) return;
                                  setEnableConfirm(false);
                                  setTimeout(() => onSubmitForm(), 100);
                                }}
                                height={'44px'}
                              >
                                <span style={{ marginRight: 8 }}>
                                  {translations
                                    ? translations['b2c.txt.confirm']
                                    : ''}
                                </span>
                                <i className="fa-solid fa-arrow-right"></i>
                              </StyledButton>
                            </div>
                          </>
                        )}
                        {!isValidPaymentTime && (
                          <div
                            className="--sub-title"
                            style={{ fontStyle: 'normal', textAlign: 'center' }}
                          >
                            {translations
                              ? `${translations[
                                'b2b.txt.note-transfer-money-invalid'
                              ]?.replace('${payment}', payment.name)}`
                              : ''}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                {/* cash | banktransfer */}
                {payment?.code &&
                  payment?.code.toLowerCase() === 'banktransfer' && (
                    <>
                      <div className="checkOutInfo">
                        <HeaderContainer>
                          <Title>
                            {translations
                              ? translations[
                                'b2c.txt.create-order-number'
                              ]?.replaceAll('${number}', `${idOrder ?? ''}`)
                              : ''}
                          </Title>
                        </HeaderContainer>
                        {!isHidePaymentDesc && (
                          <Description>
                            {expiredHours ? (
                              <>
                                {translations
                                  ? replacePaymentWithSpan(
                                    translations[
                                    'b2c.txt.pay-order-description'
                                    ],
                                    '${payment}',
                                    payment?.name ?? '',
                                    { fontWeight: 'bold' },
                                  )
                                  : ''}{' '}
                                {translations
                                  ? replacePaymentWithSpan(
                                    translations[
                                    'b2c.txt.pay-order-description-1'
                                    ],
                                    '${hour}',
                                    expiredHours + 'h',
                                    { color: '#E92127' },
                                  )
                                  : ''}
                              </>
                            ) : (
                              <>
                                {translations &&
                                  replacePaymentWithSpan(
                                    translations[
                                    'b2c.txt.pay-order-description'
                                    ],
                                    '${payment}',
                                    translations['b2b.txt.bank-transfer'],
                                    {
                                      fontWeight: 'bold',
                                      textTransform: 'uppercase',
                                    },
                                  )}
                                {'. '}
                                {translations
                                  ? translations[
                                  'b2b.txt.pay-order-description-2'
                                  ]
                                  : ''}
                              </>
                            )}
                          </Description>
                        )}
                      </div>
                      <div className="checkOutInfo">
                        <HeaderContainer>
                          <Title>
                            {translations
                              ? translations['b2b.txt.paymentGuide']
                              : ''}
                          </Title>
                        </HeaderContainer>
                        {isValidPaymentTime && (
                          <>
                            <Description>
                              {translations
                                ? replacePaymentWithSpan(
                                  translations[
                                  'b2b.form-policy.bank_descriptions'
                                  ],
                                  '${totalPrice}',
                                  paymentTotalLabel,
                                  { fontWeight: 'bold', color: '#E92127' },
                                )
                                : ''}
                            </Description>
                            <Row nz-row className="d-flex txt-content">
                              <Col xs={8} md={4}>
                                {translations
                                  ? `${translations['b2b.txt.bank-account']}: `
                                  : ''}
                              </Col>
                              <Col xs={14} md={18}>
                                {bankInfo?.bankAccount}
                              </Col>
                            </Row>
                            <Row nz-row className="d-flex txt-content">
                              <Col xs={8} md={4}>
                                {translations
                                  ? `${translations['b2c.txt.bank']}: `
                                  : ''}
                              </Col>
                              <Col xs={14} md={18}>
                                {bankInfo?.bankName}
                              </Col>
                            </Row>
                            <Row nz-row className="d-flex txt-content">
                              <Col xs={8} md={4}>
                                {translations
                                  ? `${translations['b2c.txt.modal-ticket-content']}: `
                                  : ''}
                              </Col>
                              <Col xs={14} md={18}>
                                {idOrder}
                              </Col>
                            </Row>
                          </>
                        )}
                        <div
                          className="--sub-title"
                          style={
                            isValidPaymentTime
                              ? { margin: '17px 0px' }
                              : { fontStyle: 'normal', textAlign: 'center' }
                          }
                        >
                          {isValidPaymentTime
                            ? translations
                              ? `${translations['b2b.txt.note-transfer-money']}: `
                              : ''
                            : translations
                              ? `${translations[
                                'b2b.txt.note-transfer-money-invalid'
                              ]?.replace('${payment}', payment?.name ?? '')}`
                              : ''}
                        </div>
                        {isValidPaymentTime && (
                          <>
                            <div className="d-flex txt-content">
                              {translations
                                ? translations['b2b.txt.QR-code']
                                : ''}
                            </div>
                            <ImgWrapper>
                              <div className="d-flex justify-content-center">
                                <StyledImg
                                  src={bankInfo?.qrCode?.original}
                                  alt=""
                                />
                              </div>
                            </ImgWrapper>
                          </>
                        )}

                        <StyledDivider
                          style={{
                            margin: '20px 0px 25px 0px',
                          }}
                          dashed
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <StyledButton
                            bgColor="white"
                            border="1px solid #808080"
                            textColor="#808080"
                            height={'44px'}
                            onClick={() => handleRidrect()}
                            style={{
                              marginRight: 16,
                              width: 'auto',
                              minWidth: 160,
                            }}
                          >
                            {translations
                              ? translations['b2b.txt.back-to-history']
                              : ''}
                          </StyledButton>
                        </div>
                      </div>
                    </>
                  )}
              </div>
              <div className="col-xl-4">
                <CheckoutOrderOverview
                  vouchers={vouchers}
                  headerText={`${translations?.['b2b.txt.order'] ?? ''}: ${idOrder ?? ''
                    }`}
                  isDisableCountDown={
                    !Boolean(orderCheckout?.current?.expiredAt)
                  }
                  listCart={listCart}
                  orderCheckoutRef={orderCheckout}
                  isHideButton={true}
                  listGroupCart={groupCart}
                  totalAmount={totalAmount}
                  totalDiscount={totalDiscount}
                  totalPrice={totalPrice}
                  totalPriceOriginal={totalPriceOriginal}
                  onTimeout={() => setIsShowTimeout(true)}
                  onCheckout={() => { }}
                  extraTitle={
                    isEditable ? (
                      <button
                        className="mb-3"
                        style={{
                          color: isEnableAdjustOrder ? '#e92127' : '#9d9d9d',
                          fontWeight: 500,
                          userSelect: 'none',
                          background: 'transparent',
                          border: 'none',
                          outline: 'none',
                          padding: 0,
                        }}
                        onClick={onOpenEditModal}
                        disabled={!isEnableAdjustOrder}
                      >
                        {translations?.['b2b.btn.edit'] ?? ''}
                      </button>
                    ) : null
                  }
                />
              </div>
            </div>
          </LoadingContainer>
        </div>
      </div>
      {/* <ExitConfirm
        message={translations ? translations['b2c.txt.directOut'] : ''}
        enable={enableConfirm}
      /> */}
      <TimeoutModal
        open={isShowTimeout}
        onClose={() => {
          onCloseDialogNoti();
          history.push(
            '/restaurants/payment-callback?vnp_TxnRef=' +
            orderCheckout.current?.idHash,
          );
        }}
      />
      <FormProvider {...editOrderForm}>
        <EditOrderPopup
          open={openEditModal}
          onClose={onCloseEditModal}
          groupTicketData={groupCart}
          idHash={order?.idHash ?? ''}
          getOrderDetail={getOrderDetail}
          buyingFlow={
            isEditableInProxyBuyingFlow
              ? EDIT_FLOW_TYPES.PROXY_BUYING
              : EDIT_FLOW_TYPES.NORMAL_BUYING
          }
        />
      </FormProvider>
    </CheckoutWrapper>
  );
});

export const TimeoutModal = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose?: () => void;
}) => {
  const translations = useSelector(selectTranslations);
  return (
    <ModalContainer
      isShow={open}
      maskClosable={false}
      onCloseDialog={onClose}
      CloseIcon={<CloseIcon width={12} height={12} />}
      styleSlots={{
        iconWrapperStyle: {
          margin: '-8px 0px 0px 28px',
          width: 12,
          height: 12,
        },
        modalContentStyle: {
          height: 'auto',
          width: 'auto',
          padding: '38px 31px',
          borderRadius: '16px',
        },
        modalStyle: {
          width: 'auto',
          maxWidth: '280px',
        },
        bodyStyle: {
          width: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        },
      }}
    >
      <div>{translations ? translations['b2b.txt.timeoutPayment'] : ''}</div>
      <div>{translations ? translations['b2b.txt.retry'] : ''}</div>
    </ModalContainer>
  );
};

const Policy = styled.div`
  background: #ffffff;
  overflow: auto;
  height: 600px;
  ::-webkit-scrollbar {
    width: 6px; /* Width of the scrollbar */
  }

  /* Define the track (the area behind the thumb) */
  ::-webkit-scrollbar-track {
    background: transparent; /* Color of the track */
  }

  /* Define the scrollbar thumb */
  ::-webkit-scrollbar-thumb {
    background: #ececec; /* Color of the thumb */
    border-radius: 20px;
  }

  /* Define the scrollbar thumb on hover */
  ::-webkit-scrollbar-thumb:hover {
    background: #d6d6d6; /* Color of the thumb on hover */
  }
`;

const StyledPolicy = styled.div`
  border-radius: 20px;
  background: #ffffff;
  /* height: 301px; */
  padding: 24px 20px;
  border: 1px solid #d9d9d9;
  color: #000000;
  margin-top: 12px;
`;

const Title = styled.h4`
  margin: 0;
  font-weight: 600;
  color: #000000;
  font-size: 24px;
  text-transform: capitalize;
`;

const Description = styled.div`
  font-weight: 400;
  color: #000000;
  font-size: 14px;
  text-align: left;
  margin-top: 14px;
  line-height: 1.7rem;
`;

const HeaderContainer = styled.div`
  margin: 10px 0px;
  display: flex;
  justify-content: center;
  color: #000000;
`;
const SubHeaderContainer = styled.div`
  margin: 10px 0px;
  display: flex;
  color: #000000;
  font-size: 16px;
  font-weight: 600;
`;

const StyledDivider = styled(Divider)`
  margin: 14px 0px 14px 0px;
  width: 100%;
  min-width: 100%;
`;
const ImgWrapper = styled.div`
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
`;
const StyledImg = styled.img`
  width: 250px;
  max-width: 100%;
  height: 'auto';
  display: 'flex';
  justify-content: 'center';
  object-fit: cover;
  &[src=''] {
    opacity: 0;
  }
`;
const ReservationCheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) 360px;
  gap: 28px;
  align-items: start;
  max-width: 1080px;
  margin: 48px auto 80px;
  font-size: 14px;
  font-weight: 400;
  color: #000000;

  @media (max-width: 991px) {
    grid-template-columns: 1fr;
    margin: 24px auto;
  }
`;

const ReservationPolicyCard = styled.div`
  min-height: 640px;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
  padding: 40px 36px 32px;
  text-align: center;
`;

const PaymentIconWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  img {
    width: 74px;
    height: 74px;
    object-fit: contain;
  }
`;

const ReservationStatusTitle = styled.h1`
  margin: 0 0 18px;
  font-size: 20px;
  font-weight: 600;
  color: #000000;
`;

const ReservationCodeLine = styled.div`
  font-size: 14px;
  color: #000000;

  strong {
    color: #e92127;
    font-weight: 600;
  }
`;

const ReservationNote = styled.div`
  max-width: 520px;
  margin: 22px auto 24px;
  font-size: 14px;
  line-height: 1.7rem;
  color: #000000;

  strong {
    font-weight: 600;
  }
`;

const ReservationDivider = styled.div`
  height: 1px;
  background: #e7e7e7;
  margin: 0 0 24px;
`;

const PolicyTitle = styled.h2`
  margin: 0 0 22px;
  font-size: 24px;
  font-weight: 600;
  color: #000000;
`;

const PolicyDescription = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.7rem;
  color: #000000;
`;

const ReservationPolicyBox = styled.div`
  height: 301px;
  overflow: auto;
  border: 1px solid #d9d9d9;
  border-radius: 20px;
  padding: 24px 20px;
  text-align: left;
  color: #000000;
`;

const PolicySubTitle = styled.div`
  margin-bottom: 12px;
  font-size: 16px;
  font-weight: 600;
`;

const PolicyText = styled.div`
  font-size: 14px;
  line-height: 1.7rem;
  white-space: pre-wrap;
`;

const ReservationCheckbox = styled(Checkbox)`
  display: flex;
  margin-top: 18px;
  font-size: 14px;
  color: #000000;
`;

const ReservationActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
`;

const HistoryButton = styled.button`
  min-width: 162px;
  height: 44px;
  border: 1px solid #808080;
  border-radius: 4px;
  background: #ffffff;
  color: #808080;
  font-size: 14px;
`;

const ConfirmButton = styled.button`
  min-width: 152px;
  height: 44px;
  border: 0;
  border-radius: 4px;
  background: linear-gradient(79.97deg, #f06f24 -7.79%, #f8b117 107.61%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.35;
  }
`;

const ReservationSummaryCard = styled.div`
  border-radius: 20px;
  background: #ffffff;
  padding: 28px 22px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.08);
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 22px;
`;

const SummaryOrder = styled.div`
  font-size: 14px;
  font-weight: 600;

  strong {
    color: #e92127;
  }
`;

const SummaryTimer = styled.div`
  min-width: 92px;
  border-radius: 999px;
  background: #ffe5d4;
  color: #f06f24;
  padding: 7px 10px;
  text-align: center;
  font-size: 14px;
  font-weight: 600;
`;

const RestaurantTicket = styled.div`
  overflow: hidden;
  border: 2px solid #f8b117;
  border-radius: 8px;
`;

const TicketHead = styled.div`
  background: #f8b117;
  padding: 12px 14px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
`;

const TicketBody = styled.div`
  padding: 14px;
  background: #fffdf7;
`;

const TicketName = styled.div`
  margin-bottom: 12px;
  font-size: 14px;
  font-weight: 600;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 8px;
  font-size: 14px;

  span {
    color: #666666;
  }

  strong {
    text-align: right;
    color: #111111;
  }
`;

const PaymentSummary = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 24px 0;
  font-size: 14px;
  font-weight: 700;

  strong {
    color: #e92127;
  }
`;

const PolicySummaryTitle = styled.div`
  margin-bottom: 14px;
  font-size: 16px;
  font-weight: 600;
`;

const PolicySummaryItem = styled.div`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;

  &:before {
    content: '✓';
    margin-right: 8px;
    color: #f06f24;
  }
`;
